import React from 'react';
import { Divider, Typography, Button, Checkbox, Modal, Input, notification, Form, Select, Radio, DatePicker } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TitleComponent from '../../Components/Title/Title';
import KeyValuePair from './KeyValuePair';
import StyledCard from '../../Components/StyledCard/StyledCard';
import Esign from '../../../../components/Esign';
import { getWorkmanById, updateWorkmanProfile } from '../../../../services/selfOnboarding';
import { getSignedUrl } from '../../../../services/auth';
import DocumentPreview from '../../../../components/DocumentPreview';
import I9Form from '../../I9Form';
import RejectModal from './RejectModal';
import { valueMap } from '../../mapping';
import dayjs from 'dayjs';
import FormsList from '../../Components/FormsList';
import useAuth from '../../../../hooks/useAuth';


const { Title } = Typography;

const documentCategories = {
  'IRS Compliance': ['Form W-9', 'Form W-4', 'irsCompliance', 'IRS Compliance'],
  'I9 Compliance': ['I-9 Form', 'i9Complinace', 'I9 Compliance'],
  'OSHA Compliance': ['OSHA Compliance'],
  'State/Role-Specific Compliance': ['State/Role-specific Compliance'],
};

const WorkmanDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userConfig } = useAuth();
  const { workmanId } = useParams();
  const [workman, setWorkman] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [categorizedDocuments, setCategorizedDocuments] = React.useState({});
  const [preview, setPreview] = React.useState({});
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [esignature, setEsignature] = React.useState(null);
  const [finalCheckbox, setFinalCheckbox] = React.useState(false);
  const [openI9Template, setOpenI9Template] = React.useState(false);
  const [rejectModalOpen, setRejectModalOpen] = React.useState(false);
  const [rejectRemark, setRejectRemark] = React.useState('');
  const [actionLoading, setActionLoading] = React.useState(false);

  // For List A, List B and List C checkboxes
  const [listAChecked, setListAChecked] = React.useState(false);
  const [listBChecked, setListBChecked] = React.useState(false);
  const [listCChecked, setListCChecked] = React.useState(false);
  const [listType, setListType] = React.useState(null);
  const [form] = Form.useForm();

  // Enable buttons only if all checkboxes and esignature are done
  const canProceed = finalCheckbox && !!esignature && !workman?.transactionStatus?.includes('Payroll');

  React.useEffect(() => {
    if (workman?.supervisorEsign) {
      setEsignature(workman.supervisorEsign);
    }
  }, [workman]);

  React.useEffect(() => {
    if (workmanId) {
      getWorkmanById(workmanId)
        .then((res) => {
          setWorkman(res.data?.data);
          setCategorizedDocuments(categorizeDocuments(documentCategories, res.data?.data?.uploadDocuments?.uploadDocuments));
          const formData = res.data?.data?.['jobDetails&I9Section2'] || {};
          form.setFieldsValue({
            ...formData,
            // Convert date strings to dayjs objects for DatePicker
            expirationDateA: formData.expirationDateA ? dayjs(formData.expirationDateA) : undefined,
            expirationDateB: formData.expirationDateB ? dayjs(formData.expirationDateB) : undefined,
            joiningDate: formData.joiningDate ? dayjs(formData.joiningDate) : undefined,
          });
          // Set listType state if present
          if (formData.listType) setListType(formData.listType);
        })
        .finally(() => setLoading(false));
    }
  }, [workmanId, form]);

  const categorizeDocuments = (documentCategories, uploadedDocuments) => {
    const categorized = {};
    const uploadedDocs = uploadedDocuments || [];

    // Initialize categories with empty arrays
    Object.keys(documentCategories).forEach((category) => {
      categorized[category] = [];
    });
    // Categorize each uploaded document
    uploadedDocs.forEach((doc) => {
      for (const [category, expectedDocs] of Object.entries(documentCategories)) {
        // Check if document name matches any in this category
        if (expectedDocs.includes(doc.name)) {
          categorized[category].push(doc);
          break;
        }
      }
    });

    return categorized;
  };

  const previewFile = async (record) => {
    const { data } = await getSignedUrl(encodeURIComponent(record?.filePath));
    setPreview({
      url: data?.data,
      type: record?.type,
      name: record?.name,
    });
    setPreviewOpen(true);
  };

  const handleCancel = () => setPreviewOpen(false);

  const previewI9Template = async () => {
    setOpenI9Template(true);
  };

  const handleReject = async () => {
    setActionLoading(true);
    try {
      await updateWorkmanProfile({
        updateTransactionStatus: true,
        workmanId,
        status: 'Rejected',
        rejectionRemark: rejectRemark,
        supervisorEsign: esignature,
      });
      notification.success({ message: 'Workman rejected successfully.' });
      setRejectModalOpen(false);
      navigate('/home/i9-submissions');
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Failed to reject workman.' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    setActionLoading(true);
    form.validateFields().then(async (values) => {
      const formattedValues = {
        ...values,
        expirationDateA: values.expirationDateA ? dayjs(values.expirationDateA).format('YYYY-MM-DD') : undefined,
        expirationDateB: values.expirationDateB ? dayjs(values.expirationDateB).format('YYYY-MM-DD') : undefined,
        joiningDate: values.joiningDate ? dayjs(values.joiningDate).format('YYYY-MM-DD') : undefined,
        // Add more fields here if needed
      };
      const supervisorSubmission = { ...formattedValues };
      await updateWorkmanProfile({
        updateTransactionStatus: true,
        workmanId,
        status: 'Supervisor Verified',
        supervisorEsign: esignature,
        supervisorSubmission,
        authorizedRepresentativeName: `${userConfig?.firstName} ${userConfig?.lastName}`,
        authorizedRepresentativeTitle: userConfig?.role?.charAt(0).toUpperCase() + userConfig?.role?.slice(1)
      });
      notification.success({ message: 'Workman approved and sent to payroll system.' });
      navigate('/home/i9-submissions');
    }).catch((err) => {
    notification.error({ message: err?.response?.data?.message || 'Fill in the required fields.' });
    }).finally(() => {
      setActionLoading(false);
    });
  }

  // console.log('Workman Details:', workman);
  const jobDetails = workman?.['jobDetails&I9Section1'] || {};
  if (loading) return <div>Loading...</div>;
  return (
    <>
      <div style={{ paddingRight: 50, position: 'relative', minHeight: '100vh' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <Button type="text" icon={<LeftOutlined />} onClick={() => navigate('/home/i9-submissions')} style={{ marginRight: 8 }} />
          <Title level={3} style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>
            {t('i9Submissions.workmanDetails')}
          </Title>
        </div>

        <Title level={3} style={{ fontSize: 18, fontWeight: 600 }}>
          {t('i9Submissions.sectionA')}
        </Title>

        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          <div style={{ flex: 1 }}>
            {/* Group each Title+Card in a wrapper with no gap */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>
                <TitleComponent title={t('i9Submissions.workmanDetails')} />
                <StyledCard>
                  <KeyValuePair label={t('i9Submissions.lastName')} value={jobDetails?.lastName || '-'} />
                  <KeyValuePair label={t('i9Submissions.firstName')} value={jobDetails?.firstName || '-'} />
                  <KeyValuePair label={t('i9Submissions.middleInitial')} value={jobDetails?.middleInitial || '-'} />
                  <KeyValuePair label={t('i9Submissions.otherLastNames')} value={jobDetails?.otherLastNames || '-'} />
                </StyledCard>
              </div>
              <div>
                <TitleComponent title={t('i9Submissions.location')} />
                <StyledCard>
                  <KeyValuePair label={t('i9Submissions.address')} value={jobDetails?.address || '-'} />
                  <KeyValuePair label={t('i9Submissions.city')} value={jobDetails?.city || '-'} />
                  <KeyValuePair label={t('i9Submissions.state')} value={jobDetails?.state || '-'} />
                  <KeyValuePair label={t('i9Submissions.zip')} value={jobDetails?.zipCode || '-'} />
                  <KeyValuePair label={t('i9Submissions.apartment')} value={jobDetails?.apartNumber || '---'} />
                </StyledCard>
              </div>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>
                <TitleComponent title={t('i9Submissions.contactDetails')} />
                <StyledCard>
                  <KeyValuePair label={t('i9Submissions.email')} value={workman?.email || '-'} />
                  <KeyValuePair label={t('i9Submissions.phone')} value={workman?.phone || '-'} />
                </StyledCard>
              </div>
              <div>
                <TitleComponent title={t('i9Submissions.otherDetails')} />
                <StyledCard>
                  <KeyValuePair label={t('i9Submissions.dob')} value={jobDetails?.dob} />
                  <KeyValuePair label={t('i9Submissions.ssn')} value={jobDetails?.ssnNumber || '-'} />
                </StyledCard>
              </div>
              <div>
                <TitleComponent title={t('i9Submissions.citizenshipStatus')} />
                <StyledCard>
                  <KeyValuePair
                    label={t('i9Submissions.status')}
                    value={(jobDetails?.citizenshipStatus && valueMap[jobDetails?.citizenshipStatus]) || '-'}
                  />
                  {jobDetails?.citizenshipStatus === 'presidentResident' && (
                    <>
                      <KeyValuePair label={t('onboarding.aNumberLabel')} value={jobDetails?.citizenshipNumber || '-'} />
                    </>
                  )}
                  {(jobDetails?.citizenshipStatus === 'temporaryVisa' && jobDetails.documentType !== 'passportNumber') && (
                    <>
                      <KeyValuePair label={t('onboarding.documentTypeLabelSummary')} value={valueMap[jobDetails?.documentType] || '-'} />
                      <KeyValuePair label={t('onboarding.citizenshipNumberLabel')} value={jobDetails?.citizenshipNumber || '-'} />
                      <KeyValuePair label={t('onboarding.expirationDateLabel')} value={jobDetails?.expirationDate} />
                    </>
                  )}
                  {(jobDetails?.citizenshipStatus === 'temporaryVisa' && jobDetails.documentType === 'passportNumber') && (
                    <>
                      <KeyValuePair label={t('onboarding.documentTypeLabelSummary')} value={valueMap[jobDetails?.documentType] || '-'} />
                      <KeyValuePair label={t('onboarding.citizenshipNumberLabel')} value={jobDetails?.citizenshipNumber || '-'} />
                      <KeyValuePair label={t('onboarding.expirationDateLabel')} value={jobDetails?.expirationDate} />
                      <KeyValuePair label={t('onboarding.countryOfIssuanceShortLabel')} value={jobDetails?.countryOfIssuance || '-'} />
                    </>
                  )}
                </StyledCard>
              </div>
            </div>
          </div>
        </div>

        {/* <Title level={3} style={{ fontSize: 18, fontWeight: 600 }}>W4</Title> */}

        {/* <div style={{ display: 'flex', gap: 8, marginBottom: 24, width: '50%' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>
                <TitleComponent title={t('i9Submissions.Details')} />
                <StyledCard>
                  <KeyValuePair label={t('i9Submissions.filingStatus')} value={w4Details?.filingStatus || '-'} />
                  <KeyValuePair label={t('i9Submissions.taxCredit')} value={w4Details?.taxCredit || '-'} />
                  <KeyValuePair label={t('i9Submissions.otherIncome')} value={w4Details?.otherIncome || '-'} />
                  <KeyValuePair label={t('i9Submissions.totalDeductions')} value={w4Details?.totalDeductions || '-'} />
                  <KeyValuePair label={t('i9Submissions.extraWithholding')} value={w4Details?.extraWithholding || '-'} />
                </StyledCard>
              </div>
            </div>
          </div>
        </div> */}

        {/* <Title level={3} style={{ fontSize: 18, fontWeight: 600 }}>{t('i9Submissions.sectionB')}</Title> */}

        {/* <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {i9Documents?.selectList === 'listA' && (
                <div>
                  <TitleComponent
                    title={t('i9Submissions.listA')}
                    hasCheckbox
                    checkboxLabel={t('i9Submissions.checkboxList')}
                    checkboxProps={{
                      checked: listAChecked,
                      onChange: e => setListAChecked(e.target.checked)
                    }}
                  />
                  <StyledCard>
                    <KeyValuePair
                      label={t('i9Submissions.identityDocument')}
                      value={(i9Documents?.listDocument && valueMap[i9Documents?.listDocument]) || '-'}
                    />
                    <KeyValuePair
                      label={t('i9Submissions.documentNumber')}
                      value={i9Documents?.documentNumber1 || '-'}
                    />
                    <KeyValuePair
                      label={t('i9Submissions.expirationDate')}
                      value={i9Documents?.expirationDate1 ? new Date(workman.i9Documents.expirationDate1).toLocaleDateString() : '-'}
                    />
                  </StyledCard>
                </div>
              )}
              {i9Documents?.selectList === 'listBc' && (<>
                <div>
                  <TitleComponent
                    title={t('i9Submissions.listB')}
                    hasCheckbox
                    checkboxLabel={t('i9Submissions.checkboxList')}
                    checkboxProps={{
                      checked: listBChecked,
                      onChange: e => setListBChecked(e.target.checked)
                    }}
                  />
                  <StyledCard>
                    <KeyValuePair
                      label={t('i9Submissions.identityDocument')}
                      value={(i9Documents?.identityDocumnet && valueMap[i9Documents?.identityDocumnet]) || '-'}
                    />
                    <KeyValuePair
                      label={t('i9Submissions.documentNumber')}
                      value={i9Documents?.documentNumber2 || '-'}
                    />
                    <KeyValuePair
                      label={t('i9Submissions.expirationDate')}
                      value={i9Documents?.expirationDate2 ? new Date(workman.i9Documents.expirationDate2).toLocaleDateString() : '-'}
                    />
                    <KeyValuePair
                      label={t('i9Submissions.issuingAuthority')}
                      value={i9Documents?.issuingAuthority1 || '-'}
                    />
                  </StyledCard>
                </div>
                <div>
                  <TitleComponent
                    title={t('i9Submissions.listC')}
                    hasCheckbox
                    checkboxLabel={t('i9Submissions.checkboxList')}
                    checkboxProps={{
                      checked: listCChecked,
                      onChange: e => setListCChecked(e.target.checked)
                    }}
                  />
                  <StyledCard>
                    <KeyValuePair
                      label={t('i9Submissions.workAuthorizationDocument')}
                      value={(i9Documents?.workAuthorizationDocument && valueMap[i9Documents?.workAuthorizationDocument]) || '-'}
                    />
                    <KeyValuePair
                      label={t('i9Submissions.documentNumber')}
                      value={i9Documents?.documentNumber3 || '-'}
                    />
                    <KeyValuePair
                      label={t('i9Submissions.issuingAuthority')}
                      value={i9Documents?.issuingAuthority2 || '-'}
                    />
                  </StyledCard>
                </div>
              </>)}
            </div>
          </div>
          {/* <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>
                <TitleComponent
                  title={t('i9Submissions.documentsUploaded')}
                />
                <StyledCard>
                  {Object.entries(categorizedDocuments).map(([label, value]) => (
                    <KeyValuePair
                      key={label}
                      label={t(`i9Submissions.${label.replace(/\s/g, '').replace(/[^a-zA-Z0-9]/g, '')}`) || label}
                      value={value}
                      hasPreview={value.length > 0}
                      previewFile={value.length > 0 ? previewFile : () => {}}
                    />
                  ))}
                  <KeyValuePair
                    key={'Auto Generated I9 form'}
                    label={t('i9Submissions.autoGeneratedI9Form')}
                    value={[{name: 'I9 Form'}]}
                    hasPreview={true}
                    previewFile={previewI9Template}
                  />
                </StyledCard>
              </div>
            </div>
          </div> */}
        {/* </div> */}
        <div style={{ width: '50%' }}>
          <FormsList data={workman} />
        </div>

        <Form form={form} layout="vertical" style={{ borderRadius: 8, marginTop: 32 }}>
          <Title level={3} style={{ fontSize: 18, fontWeight: 600 }}>
            {t('i9Submissions.jobDetails')}
          </Title>
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item
              name="branch"
              label={t('i9Submissions.branch')}
              style={{ flex: 1 }}
              // rules={[{ required: true, message: 'Please select union name' }]}
            >
              <Select
                placeholder={t('i9Submissions.selectbranch')}
                options={
                  [
                    { label: "1001-Freeman - Dallas", value: '1001-freeman-dallas' },
                    { label: "1003-Freeman - San Antonio", value: '1003-freeman-sanAntonio' },
                    { label: "1004-Freeman - New Orleans", value: '1004-freeman-newOrleans' },
                    { label: "1010-Freeman - Atlanta", value: '1010-freeman-atlanta' },
                    { label: "1011-Freeman - Las Vegas", value: '1011-freeman-lasVegas' },
                    { label: "1014-Freeman - Chicago", value: '1014-freeman-chicago' },
                  ]
                }
              />
            </Form.Item>
            <Form.Item
              name="union"
              label={t('i9Submissions.union')}
              style={{ flex: 1 }}
              // rules={[{ required: true, message: 'Please select worker type' }]}
            >
              <Select
                placeholder={t('i9Submissions.selectUnion')}
                options={
                  [
                    { label: "329", value: '329' },
                    { label: "429", value: '429' },
                    { label: "1839", value: '1839' },
                    { label: "1846", value: '1846' },
                    { label: "834", value: '834' },
                    { label: "357", value: '357' },
                  ]
                }
              />
            </Form.Item>
            <Form.Item
              name="payScaleGroup"
              label={t('i9Submissions.payScaleGroup')}
              style={{ flex: 1 }}
              // rules={[{ required: true, message: 'Please select job skill' }]}
            >
              <Select
                placeholder={t('i9Submissions.selectpayScaleGroup')}
                options={
                  [
                    { label: "9K01-01", value: '9K01-01' },
                    { label: "3202-01", value: '3202-01' },
                    { label: "3202-02", value: '3202-02' },
                    { label: "3202-03", value: '3202-03' },
                    { label: "3202-04", value: '3202-04' },
                    { label: "3202-05", value: '3202-05' },
                  ]
                }
              />
            </Form.Item>
            <Form.Item
              name="level"
              label={t('i9Submissions.level')}
              style={{ flex: 1 }}
              // rules={[{ required: true, message: 'Please select job role' }]}
            >
              <Select
                placeholder={t('i9Submissions.selectLevel')}
                options={
                  [
                    { label: "1", value: '1' },
                    { label: "2", value: '2' },
                    { label: "3", value: '3' },
                    { label: "4", value: '4' },
                    { label: "5", value: '5' },
                    { label: "6", value: '6' },
                  ]
                }
              />
            </Form.Item>
          </div>

          <Title level={3} style={{ fontSize: 18, fontWeight: 600 }}>
            {t('i9Submissions.i9DocumentsSectionB')}
          </Title>

          <Form.Item
            name="listType"
            label={t('i9Submissions.selectList')}
            style={{ marginTop: 16 }}
            rules={[{ required: true, message: 'Please select list type' }]}
          >
            <Radio.Group value={listType} onChange={(e) => setListType(e.target.value)}>
              <Radio value="listA">{t('i9Submissions.listA')}</Radio>
              <Radio value="listBC">{t('i9Submissions.listBC')}</Radio>
            </Radio.Group>
          </Form.Item>
          {listType === 'listA' && (
            <>
              <Title style={{ fontSize: 15, fontWeight: 600 }}>{t('i9Submissions.listA')}</Title>
              <div style={{ display: 'flex', gap: 16 }}>
                <Form.Item
                  name="documentTypeA"
                  label={t('i9Submissions.documentType')}
                  style={{ flex: 1 }}
                  rules={[{ required: true, message: 'Please select document type' }]}
                >
                  <Select
                    placeholder={t('i9Submissions.selectDocumentType')}
                    options={[
                      { label: 'Passport', value: 'passport' },
                      { label: 'Permanent Resident Card', value: 'permanentResidentCard' },
                      { label: 'Employment Authorization Document', value: 'empAuthDoc' },
                    ]}
                  />
                </Form.Item>
                <Form.Item name="documentNumberA" label={t('i9Submissions.documentNumber')} style={{ flex: 1 }}>
                  <Input placeholder={t('i9Submissions.enterDocumentNumber')} />
                </Form.Item>
                <Form.Item name="expirationDateA" label={t('i9Submissions.expirationDate')} style={{ flex: 1 }}>
                  <DatePicker style={{ width: '100%' }} disabledDate={(current) => current && current < dayjs().startOf('day')} />
                </Form.Item>
              </div>
            </>
          )}
          {listType === 'listBC' && (
            <>
              <Title style={{ fontSize: 15, fontWeight: 600 }}>{t('i9Submissions.listB')}</Title>
              <div style={{ display: 'flex', gap: 16 }}>
                <Form.Item
                  name="identityDocumentB"
                  label={t('i9Submissions.identityDocument')}
                  style={{ flex: 1 }}
                  rules={[{ required: true, message: 'Please select identity document' }]}
                >
                  <Select
                    placeholder={t('i9Submissions.selectIdentityDocument')}
                    options={[
                      { label: 'Driver License', value: 'dl' },
                      { label: 'State ID', value: 'stateid' },
                      { label: 'Voter Registration Card', value: 'voterRegistrationCard' },
                      { label: 'School ID', value: 'schoolId' },
                      { label: 'Military Card', value: 'militaryCard' },
                      { label: 'Military Dependents ID', value: 'militaryDependentsId' },
                      { label: 'Merchant Mariner Card', value: 'merchantMarinerCard' },
                      { label: 'Native American Tribe Document', value: 'nativeAmericanTribeDoc' },
                      { label: "Canadian Driver's License", value: 'canadianDriverLicense' },
                    ]}
                  />
                </Form.Item>
                <Form.Item name="documentNumberB" label={t('i9Submissions.documentNumber')} style={{ flex: 1 }}>
                  <Input placeholder={t('i9Submissions.enterDocumentNumber')} />
                </Form.Item>
                <Form.Item name="expirationDateB" label={t('i9Submissions.expirationDate')} style={{ flex: 1 }}>
                  <DatePicker style={{ width: '100%' }} disabledDate={(current) => current && current < dayjs().startOf('day')} />
                </Form.Item>
                <Form.Item name="issuingAuthorityB" label={t('i9Submissions.issuingAuthority')} style={{ flex: 1 }}>
                  <Input placeholder={t('i9Submissions.enterIssuingAuthority')} />
                </Form.Item>
              </div>
              <Title style={{ fontSize: 15, fontWeight: 600 }}>{t('i9Submissions.listC')}</Title>
              <div style={{ display: 'flex', gap: 16 }}>
                <Form.Item
                  name="workAuthDocumentC"
                  label={t('i9Submissions.workAuthDocument')}
                  style={{ flex: 1 }}
                  rules={[{ required: true, message: 'Please select work authorization document' }]}
                >
                  <Select
                    placeholder={t('i9Submissions.selectWorkAuthDocument')}
                    options={[
                      { label: 'Social Security Card', value: 'ssn' },
                      { label: 'Certification of Birth Abroad', value: 'certificationOfBirthAbroad' },
                      { label: 'Certificate of Report of Birth', value: 'certificateOfReportOfBirth' },
                      { label: 'Birth Certificate', value: 'birthcert' },
                      { label: 'Native American Tribe Document', value: 'nativeAmericanTribeDoc' },
                      { label: 'U.S. Citizen ID Card', value: 'usCitizenIdCard' },
                      { label: 'Resident Citizen ID Card', value: 'residentCitizenIdCard' },
                    ]}
                  />
                </Form.Item>
                <Form.Item name="documentNumberC" label={t('i9Submissions.documentNumber')} style={{ flex: 1 }}>
                  <Input placeholder={t('i9Submissions.enterDocumentNumber')} />
                </Form.Item>
                <Form.Item name="issuingAuthorityC" label={t('i9Submissions.issuingAuthority')} style={{ flex: 1 }}>
                  <Input placeholder={t('i9Submissions.enterIssuingAuthority')} />
                </Form.Item>
              </div>
            </>
          )}
          <Form.Item name="joiningDate" label={<span style={{ fontSize: "16px", fontWeight: 600 }}>{t('i9Submissions.joiningDate')}</span>} style={{ flex: 1 }}  rules={[{ required: true, message: 'Please select Joining Date' }]}>
            <DatePicker style={{ width: "20%"}} />
          </Form.Item>
        </Form>

        <div style={{ marginTop: 24 }}>
          <Esign esignature={esignature} setEsignature={setEsignature} />
        </div>
        <div
          style={{
            background: 'white',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            marginTop: 24,
            minWidth: 0,
            padding: 0,
          }}
        >
          <Checkbox
            checked={finalCheckbox}
            onChange={(e) => setFinalCheckbox(e.target.checked)}
            style={{
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginTop: 2,
            }}
          />
          <span
            style={{
              fontSize: 14,
              lineHeight: '20px',
              letterSpacing: '1%',
            }}
          >
            {t('i9Submissions.checkboxFinal')}
          </span>
        </div>
        <div
          style={{
            marginTop: 32,
            display: 'flex',
            gap: 16,
            justifyContent: 'flex-end',
          }}
        >
          <Button
            style={{
              background: '#F5F5F5',
              color: '#222',
              border: 'none',
              minWidth: 100,
              fontWeight: 500,
            }}
            disabled={!canProceed}
            onClick={() => setRejectModalOpen(true)}
            loading={actionLoading}
          >
            {t('i9Submissions.reject')}
          </Button>
          <Button
            type="primary"
            style={{
              minWidth: 150,
              fontWeight: 500,
            }}
            disabled={!canProceed}
            onClick={handleApprove}
            loading={actionLoading}
          >
            {t('i9Submissions.approve')}
          </Button>
        </div>
      </div>
      <Modal open={previewOpen} title={preview?.name} maskClosable={false} footer={null} onCancel={handleCancel}>
        <DocumentPreview url={preview?.url} fileType={preview?.type} />
      </Modal>
      <I9Form openI9Template={openI9Template} setOpenI9Template={setOpenI9Template} data={{ supervisorEsign: esignature, ...workman }} />
      {/* Reject Modal */}
      <RejectModal
        rejectModalOpen={rejectModalOpen}
        setRejectModalOpen={setRejectModalOpen}
        rejectRemark={rejectRemark}
        setRejectRemark={setRejectRemark}
        actionLoading={actionLoading}
        handleReject={handleReject}
      />
    </>
  );
};

export default WorkmanDetails;
